import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DEPRECATED_TENANT_SLUGS, LEGACY_TENANT_SLUGS } from '../api/types';
import { useGetAllTenantsQuery } from '../api/account/accountApiSlice';
import { makePermissionListSelector } from '../selector/authenticationSelector';
import { buildPermissionAliases } from './legacyTenantPermissions';
import { registerTenantDisplayNames } from './tenantDisplayNames';
import { useIsMaster } from './useIsMaster';

/**
 * Returns the list of tenant slugs the current user has access to. Used
 * by Master Mode to know which tenants to fan out queries to.
 *
 * Two sources are merged:
 *  - Legacy tenants (individual databases), according to the user's
 *    `tenant:<slug>` JWT permissions. Order matches `LEGACY_TENANT_SLUGS`
 *    so iteration is stable across renders.
 *  - Onboarded operational-DB tenants via `GET /admin/tenants` — these
 *    have no `tenant:<slug>` permission, so without this fetch the
 *    all-tenant view silently misses every migrated / self-service
 *    tenant. The endpoint is backend-gated on the `master` role, so the
 *    fetch is skipped for non-masters (whose view stays permission-based,
 *    same as before). Appended after the legacy slugs, sorted by
 *    organization name for a stable order.
 */
export const useAccessibleTenants = (): string[] => {
    const permissionSelector = useMemo(() => makePermissionListSelector(), []);
    const permissions = useSelector(permissionSelector);
    const isMaster = useIsMaster();
    const { data: operationalTenants } = useGetAllTenantsQuery(undefined, {
        skip: !isMaster,
    });

    return useMemo(() => {
        const owned = new Set(permissions);
        const slugs: string[] = [];
        LEGACY_TENANT_SLUGS.forEach((slug) => {
            const aliases = buildPermissionAliases(slug);
            if (aliases.some((alias) => owned.has(alias))) {
                slugs.push(slug);
            }
        });
        // Drop the retired v1 slugs UNCONDITIONALLY (CEO, 2026-09-18). This
        // used to drop them only when the `_v2` counterpart was also granted,
        // which left one hole: a user holding only the v1 permission could
        // still select it and be routed to the stale backend. Everyone has
        // been migrated to the current app, so the v1 route is closed — see
        // DEPRECATED_TENANT_SLUGS for why they nonetheless stay in
        // LEGACY_TENANT_SLUGS.
        const legacySlugs = slugs.filter(
            (slug) => !DEPRECATED_TENANT_SLUGS.has(slug),
        );

        // Append the operational-DB tenants, skipping any slug that
        // collides with a hardcoded legacy tenant (same dedupe the
        // TenantSwitcher applies — the sets are expected disjoint).
        const merged = [...legacySlugs];
        const seen = new Set(legacySlugs);
        // Archived tenants (#1379) are in the directory so the switcher can
        // offer them for reactivation; Master mode's all-tenant views are
        // about live tenants and leave them out, as before the directory
        // carried them.
        const sortedOperational = [...(operationalTenants ?? [])]
            .filter((entry) => entry.status?.toLowerCase() !== 'archived')
            .sort(
                (a, b) => a.organizationName.localeCompare(b.organizationName),
            );
        sortedOperational.forEach((entry) => {
            if (LEGACY_TENANT_SLUGS.has(entry.slug) || seen.has(entry.slug)) return;
            seen.add(entry.slug);
            merged.push(entry.slug);
        });

        // Make the operational tenants' organization names resolvable via
        // getTenantDisplayName before this render's result propagates —
        // an idempotent cache write, deliberately inside the memo so the
        // names are registered before (not after) the list re-renders.
        registerTenantDisplayNames(
            Object.fromEntries(
                sortedOperational.map((entry) => [entry.slug, entry.organizationName]),
            ),
        );

        return merged;
    }, [permissions, operationalTenants]);
};
